import { ChartContainer } from '@lms/ui/chart';
import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatBucket, formatCompact, formatNumber } from '../format';
import { PREVIOUS_COLOR, pivot, type SeriesSet } from '../series';
import {
  bucketKey,
  type Period,
  periodBuckets,
  type Step,
  stepFor,
} from '../time';
import {
  axisTick,
  ChartEmpty,
  ChartLegendList,
  MAX_BAR_SIZE,
  SeriesTooltip,
  segmentShape,
} from './parts';

export interface StackedTimeChartProps<R> {
  data: readonly R[];
  /** Backend period string of a row (`2025-03-01T00:00:00Z`). */
  period: (row: R) => string;
  /** Raw dimension value of a row, resolved through `seriesSet`. */
  series: (row: R) => string;
  value: (row: R) => number;
  seriesSet: SeriesSet;
  /** Selected period and reference date, used to fill empty buckets. */
  range: { period: Period; fromDate: Date; step?: Step };
  locale?: string;
  height?: number;
  emptyLabel?: string;
  totalLabel?: string;
  /**
   * `bars` (default): stacked columns per bucket. `area`: stacked areas, for
   * the trend over the range; ignores `comparison`.
   */
  variant?: 'bars' | 'area';
  /**
   * The same period one year earlier, drawn as outlines behind each column.
   * Its periods are last year's dates; they're shifted forward a year.
   */
  comparison?: {
    data: readonly R[];
    label: string;
  };
}

/** Key for last year's total in a pivoted row. */
const PREVIOUS = 'previous';

/** Moves a `YYYY-MM-DD` bucket key forward one year. */
function nextYear(key: string): string {
  return `${Number(key.slice(0, 4)) + 1}${key.slice(4)}`;
}

/**
 * Stacked columns (or areas) per time bucket. Every bucket in the selected
 * range is shown, including those without data, so gaps read as zero instead
 * of being silently skipped.
 */
export function StackedTimeChart<R>({
  data,
  period,
  series,
  value,
  seriesSet,
  range,
  locale,
  height = 240,
  emptyLabel = 'No data for this period',
  totalLabel,
  variant = 'bars',
  comparison: comparisonProp,
}: StackedTimeChartProps<R>) {
  const comparison = variant === 'bars' ? comparisonProp : undefined;
  const effectiveStep = range.step ?? stepFor(range.period);

  const rows = useMemo(() => {
    const keys = data.map((r) => bucketKey(period(r)));
    const buckets = periodBuckets(
      range.period,
      range.fromDate,
      keys,
      effectiveStep,
    );
    const pivoted = pivot(data, {
      category: (r) => bucketKey(period(r)),
      series: (r) => seriesSet.keyOf(series(r)),
      value,
      keys: seriesSet.series.map((s) => s.key),
      categories: buckets,
    });
    if (comparison) {
      const previous = new Map<string, number>();
      for (const r of comparison.data) {
        const k = nextYear(bucketKey(period(r)));
        previous.set(k, (previous.get(k) ?? 0) + value(r));
      }
      for (const row of pivoted)
        row[PREVIOUS] = previous.get(row.category) ?? 0;
    }
    return pivoted;
  }, [
    comparison,
    data,
    period,
    series,
    value,
    seriesSet,
    range.period,
    range.fromDate,
    effectiveStep,
  ]);

  const hasData = rows.some((r) => r.total > 0 || Number(r[PREVIOUS] ?? 0) > 0);
  if (!hasData) return <ChartEmpty label={emptyLabel} height={height} />;

  const spansYears =
    effectiveStep === 'month' &&
    rows.length > 0 &&
    rows[0].category.slice(0, 4) !== rows[rows.length - 1].category.slice(0, 4);
  const keys = seriesSet.series.map((s) => s.key);

  // Grid, axes and tooltip, the same for both variants.
  const shared = [
    <CartesianGrid key="grid" vertical={false} />,
    <XAxis
      key="x"
      dataKey="category"
      tickLine={false}
      axisLine={false}
      tickMargin={8}
      minTickGap={effectiveStep === 'day' ? 8 : 16}
      interval="preserveStartEnd"
      tick={axisTick}
      tickFormatter={(k: string) =>
        formatBucket(k, effectiveStep, { locale, withYear: spansYears })
      }
    />,
    <YAxis
      key="y"
      tickLine={false}
      axisLine={false}
      allowDecimals={false}
      width={36}
      tickCount={5}
      tick={axisTick}
      tickFormatter={(v: number) => formatCompact(v, locale)}
    />,
    <Tooltip
      key="tooltip"
      cursor={
        variant === 'area'
          ? { stroke: 'var(--muted-foreground)', strokeWidth: 1 }
          : { fill: 'var(--muted)', opacity: 0.6 }
      }
      content={
        <SeriesTooltip
          series={seriesSet.series}
          locale={locale}
          totalLabel={totalLabel}
          alwaysNamed
          title={(row) =>
            formatBucket(row.category, effectiveStep, {
              locale,
              variant: 'long',
            })
          }
          footer={
            comparison
              ? (row) => (
                  <>
                    <span className="text-muted-foreground">
                      {comparison.label}
                    </span>
                    <span className="text-foreground tabular-nums">
                      {formatNumber(Number(row[PREVIOUS] ?? 0), locale)}
                    </span>
                  </>
                )
              : undefined
          }
        />
      }
    />,
  ];

  return (
    <div className="flex flex-col gap-3">
      <ChartContainer
        config={seriesSet.config}
        className="aspect-auto w-full"
        style={{ height }}
      >
        {variant === 'area' ? (
          <AreaChart
            data={rows}
            margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
            accessibilityLayer
          >
            {shared}
            {seriesSet.series.map((s) => (
              <Area
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId="stack"
                type="monotone"
                stroke={s.color}
                strokeWidth={2}
                fill={s.color}
                fillOpacity={0.3}
                activeDot={{ r: 4, stroke: 'var(--card)', strokeWidth: 2 }}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        ) : (
          <BarChart
            data={rows}
            margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
            barCategoryGap="20%"
            barGap={2}
            accessibilityLayer
          >
            {shared}
            {comparison && (
              // Its own stack, so it stands beside this year's column (left of
              // it) instead of on top.
              <Bar
                dataKey={PREVIOUS}
                name={comparison.label}
                stackId="previous"
                fill={PREVIOUS_COLOR}
                radius={[4, 4, 0, 0]}
                maxBarSize={MAX_BAR_SIZE}
                isAnimationActive={false}
              />
            )}
            {seriesSet.series.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId="stack"
                fill={s.color}
                maxBarSize={MAX_BAR_SIZE}
                isAnimationActive={false}
                shape={segmentShape(s.key, keys, 'column')}
              />
            ))}
          </BarChart>
        )}
      </ChartContainer>
      <ChartLegendList
        series={seriesSet.series}
        comparisonLabel={comparison?.label}
        comparisonColor={PREVIOUS_COLOR}
        always
      />
    </div>
  );
}
